import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./Spartan.scss";

const SPARTAN_IMG = (file) => imageUrl("spartan", file);
const EMBED_URL = "https://productmapper-pro.lovable.app/";

const challengeItems = [
  {
    text: "Time-consuming manual product comparison and mapping",
    icon: "Clock.svg",
  },
  {
    text: "Heavy reliance on a limited number of domain experts",
    icon: "User.svg",
  },
  {
    text: "Scattered and inconsistent product data across files and systems",
    icon: "Search.svg",
  },
  {
    text: "Lack of standardised equivalent mapping across teams",
    icon: "Badge.svg",
  },
  {
    text: "Slow RFP turnaround impacting responsiveness and competitiveness",
    icon: "Timer.svg",
  },
];

const solutionItems = [
  {
    title: "Retrieve First",
    text: "If a competitor product has already been mapped and approved, the platform instantly returns the correct equivalent.",
    icon: "Bottle.svg",
  },
  {
    title: "Generate Second",
    text: "If no mapping exists, the AI assistant analyzes the competitor product details and suggests a likely equivalent for user review and approval.",
    icon: "Bulb.svg",
  },
];

const featureItems = [
  {
    title: "Competitor Information Management",
    text: "Upload competitor product data through structured Excel files and standardize it for analysis.",
    icon: "Competitor.svg",
  },
  {
    title: "Equivalent Mapping Repository",
    text: "Store and manage approved mappings between competitor and internal products for future reuse.",
    icon: "Tree.svg",
  },
  {
    title: "AI Assistant ChatBot",
    text: "Interact with the system through a conversational interface to retrieve or generate product equivalents.",
    icon: "Bot.svg",
  },
  {
    title: "AI-Generated Mapping Suggestions",
    text: "Analyse competitor product details and suggest likely equivalents with supporting reasoning.",
    icon: "Brain.svg",
  },
  {
    title: "Human-in-the-Loop Validation",
    text: "Review, confirm, or refine AI-generated mappings before saving them to the system.",
    icon: "Loop.svg",
  },
  {
    title: "Structured Data Governance",
    text: "Maintain consistent formats and controlled workflows to ensure accuracy and reduce errors.",
    icon: "DataTree.svg",
  },
];

const impactItems = [
  "Reduced RFP turnaround times by replacing manual equivalent mapping with faster AI-assisted retrieval",
  "Eliminated repetitive research by enabling instant reuse of approved product mappings",
  "Reduced dependency on subject-matter experts by centralizing knowledge within the platform",
  "Improved consistency through standardized and validated equivalent mappings across teams",
  "Scalable system that handles increasing RFP volumes without additional manual effort",
  "Enhanced competitiveness with faster, more reliable responses to customer queries",
];

const technologyItems = [
    { name: "Databricks", icon: "Databricks.svg" },
  { name: "Azure", icon: "Azure.svg" },
  { name: "SonarQube", icon: "Sonarqube.svg" },
    { name: "PostgreSQL", icon: "Sql.svg" },
  { name: "Next.js", icon: "Nextjs.svg" }
];

const Spartan = () => {
  return (
    <>
      <MegaHeader />

      <section className="spartan-hero">
        <div className="spartan-hero__media" aria-hidden="true">
          <img
            src={SPARTAN_IMG("Hero.svg")}
            alt=""
            className="spartan-hero__image"
          />
        </div>

        <div className="spartan-hero__content">
          <h1 className="spartan-hero__title">
            <span>AI-Powered Product Intelligence &amp; Equivalent Mapping Platform</span>
          </h1>
          <p className="spartan-hero__subtitle">
            <span>
              Quickly identify accurate product equivalents and respond to RFPs faster using AI-assisted retrieval and validated mappings.</span>
          </p>
        </div>
      </section>

      <section className="spartan-section spartan-challenge">
        <div className="spartan-challenge__header">
          <div className="spartan-challenge__title-wrap">
            <h2 className="spartan-challenge__title">
              <span>The</span>
              <span className="spartan-challenge__title-accent">Challenge</span>
            </h2>
          </div>
          <p className="spartan-challenge__intro">
            Commercial and RFP teams rely on manual research to identify
            product equivalents, often navigating multiple Excel files and
            consulting subject-matter experts.
          </p>
        </div>

        <div className="spartan-challenge__grid">
          {challengeItems.map((item, index) => (
            <article
              key={item.text}
              className={`spartan-challenge-card${
                index === challengeItems.length - 1
                  ? " spartan-challenge-card--wide"
                  : ""
              }`}
            >
              <img
                src={SPARTAN_IMG(item.icon)}
                alt=""
                aria-hidden="true"
                className="spartan-challenge-card__icon"
              />
              <p className="spartan-challenge-card__text">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="spartan-section spartan-solution">
        <div className="spartan-solution__header">
          <h2 className="spartan-solution__title">Solution</h2>
          <p className="spartan-solution__intro">
            The platform centralises competitor product data and approved
            mappings into a single system, combining structured data management
            with AI-assisted retrieval to quickly identify accurate equivalents
            while maintaining human validation. The platform works on a
            retrieve-first, generate-second approach.
          </p>
        </div>

        <div className="spartan-solution__grid">
          {solutionItems.map((item) => (
            <article key={item.title} className="spartan-solution-card">
              <div className="spartan-solution-card__header">
                <img
                  src={SPARTAN_IMG(item.icon)}
                  alt=""
                  aria-hidden="true"
                  className="spartan-solution-card__icon"
                />
                <h3 className="spartan-solution-card__title">{item.title}</h3>
              </div>
              <p className="spartan-solution-card__text">{item.text}</p>
            </article>
          ))}
        </div>

        <div className="spartan-solution__demo-link-row">
          <a
            href={EMBED_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="spartan-solution__demo-link"
            aria-label="View product mapper demo in a new tab"
          >
            <span>View Demo</span>
            <img
              src={SPARTAN_IMG("ViewArrow.svg")}
              alt=""
              aria-hidden="true"
              className="spartan-solution__demo-link-icon"
            />
          </a>
        </div>

        <div className="spartan-solution-embed-wrapper">
          <iframe
            src={EMBED_URL}
            title="Product Mapper"
            className="spartan-iframe"
            allowFullScreen
            allow="fullscreen"
            scrolling="yes"
          />
        </div>
      </section>

      <section className="spartan-section spartan-features">
        <div className="spartan-features__header">
          <h2 className="spartan-features__title">
            <span>Key</span>
            <span className="spartan-features__title-accent">Features</span>
          </h2>
        </div>

        <div className="spartan-features__grid">
          {featureItems.map((item) => (
            <article key={item.title} className="spartan-feature-card">
              <img
                src={SPARTAN_IMG(item.icon)}
                alt=""
                aria-hidden="true"
                className="spartan-feature-card__icon"
              />
              <div className="spartan-feature-card__content">
                <h3 className="spartan-feature-card__title">{item.title}</h3>
                <p className="spartan-feature-card__text">{item.text}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="spartan-features__tech">
          {technologyItems.map((item) => (
            <img
              key={item.name}
              src={SPARTAN_IMG(item.icon)}
              alt={item.name}
              className={`spartan-features__tech-item spartan-features__tech-item--${item.name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`}
            />
          ))}
        </div>
      </section>

      <section
        className="spartan-impact"
        style={{
          backgroundImage: `url(${SPARTAN_IMG("footer.svg")})`,
        }}
      >
        <div className="spartan-impact__overlay" aria-hidden="true" />
        <div className="spartan-impact__content">
          <h2 className="spartan-impact__title">Impact</h2>
          <ul className="spartan-impact__list">
            {impactItems.map((item) => (
              <li key={item}>
                <img
                  src={SPARTAN_IMG("Checkmark.svg")}
                  alt=""
                  aria-hidden="true"
                  className="spartan-impact__check"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};

export default Spartan;
